import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../common/Button';
import { LoadingSpinner } from '../LoadingSpinner';
import ProfileAvatar from '../common/ProfileAvatar';
import { ProfileProject } from '../../types/profile';
import CollapsibleContent from '../common/CollapsibleContent';

interface ProfileProjectHistorySectionProps {
  projects: ProfileProject[];
  loading: boolean;
  className?: string;
  hasMoreProjects?: boolean;
  fetchMoreProjects?: () => void;
}

const formatMonthYear = (d: string | Date | null | undefined, locale = 'fr-FR') => {
  if (!d) return '';
  const date = new Date(d);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
};

const ProfileProjectHistorySection = ({
  projects,
  loading,
  className,
  hasMoreProjects = false,
  fetchMoreProjects,
}: ProfileProjectHistorySectionProps) => {
  const { t, i18n } = useTranslation(['projects', 'common']);

  const projectsProfile = useMemo(
    () =>
      projects?.map(p => ({
        id: p.id,
        clientName: p.client_company_name || p.client_first_name,
        title: p.job_title,
        dateLabel: formatMonthYear(p.job_completed_at, i18n.language),
        avatarUrl: p.client_avatar_url ?? undefined,
      })),
    [projects, i18n.language]
  );

  return (
    <div
      className={`bg-white border-2 shadow-xl border-gray-300  rounded-2xl py-4 mb-6 px-6 flex flex-col ${className}`}
    >
      <CollapsibleContent
        header={
          <div className="mb-2 text-lg font-bold text-secondary">
            {t('freelancerProjectHistory', { ns: 'projects', defaultValue: 'Historique Piges' })}
          </div>
        }
      >
        {loading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {projectsProfile.length > 0 ? (
              projectsProfile.map(item => {
                const isLast = item === projectsProfile[projectsProfile.length - 1];
                return (
                  <div
                    key={item.id}
                    className={`py-3 ${isLast ? '' : 'border-b border-[#E0E0E0]'}`}
                  >
                    <div className="flex w-full items-center justify-between gap-4">
                      <div className="flex min-w-0 items-center gap-4">
                        <ProfileAvatar first_name={item.clientName} avatar_url={item.avatarUrl} />
                        <div className="min-w-0">
                          <div className="truncate font-bold text-secondary">{item.clientName}</div>
                          <div className="truncate text-sm text-secondary">{item.title}</div>
                        </div>
                      </div>
                      <div className="ml-4 shrink-0 text-sm capitalize text-secondary">
                        {item.dateLabel}
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="mt-8 text-center text-sm text-secondary">
                {t('noPastProjects', { ns: 'projects', defaultValue: 'Aucun projet existant' })}
              </div>
            )}
          </div>
        )}

        {!loading && hasMoreProjects && fetchMoreProjects && (
          <div className="mt-6 flex md:justify-end">
            <Button className="w-full md:w-auto md:self-end" onClick={fetchMoreProjects}>
              {t('loadMore', { ns: 'projects', defaultValue: 'Charger plus de résultats' })}
            </Button>
          </div>
        )}
      </CollapsibleContent>
    </div>
  );
};

export default ProfileProjectHistorySection;
